import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('hr') || SessionManager.getSession('candidate');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const HrCandidateProfilePage = () => {
  const navigate = useNavigate();
  const { candidateId } = useParams();
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [interviewModalOpen, setInterviewModalOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [inviting, setInviting] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  
  // Interview invitation state
  const [interviewData, setInterviewData] = useState({
    jobId: '',
    interviewDate: '',
    interviewTime: '',
    meetingLink: '',
    additionalNotes: ''
  });

  // Get candidate applications for showing which jobs they applied to
  const candidateApplications = candidate?.applications || [];

  // Get HR company name
  const getHrCompanyName = () => {
    SessionManager.migrateLegacySession();
    const hrSession = SessionManager.getSession('hr');
    return hrSession?.company || hrSession?.profile?.company_name || 'Company';
  };

  const hrCompanyName = getHrCompanyName();

  // Check HR authentication
  useEffect(() => {
    const isLoggedInAsHr = SessionManager.isLoggedIn('hr');
    const currentSession = SessionManager.getCurrentSession();
    
    if (!isLoggedInAsHr || (currentSession && currentSession.role !== 'hr')) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  // Fetch candidate profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!candidateId) {
        console.error('No candidate ID provided');
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching profile for candidate ID:', candidateId);
        const response = await fetch(`${API_BASE_URL}/candidates/${candidateId}/profile`, {
          headers: buildAuthHeaders(),
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Profile fetch failed:', response.status, errorText);
          throw new Error('Failed to fetch profile');
        }
        
        const data = await response.json();
        console.log('Profile data received:', data);
        setCandidate(data);
        
        // Check verification status
        try {
          const verifyResponse = await fetch(`${API_BASE_URL}/verification/status/${candidateId}`, {
            headers: buildAuthHeaders(),
          });
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            setIsVerified(verifyData.verified);
          }
        } catch (error) {
          console.error('Error checking verification:', error);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [candidateId]);

  // Fetch jobs for invite modal
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/jobs?company=${encodeURIComponent(hrCompanyName)}`, {
          headers: buildAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to fetch jobs');
        
        const data = await response.json();
        setJobs(data.jobs || []);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };

    fetchJobs();
  }, [hrCompanyName]);

  const handleSendInvite = async () => {
    setInviting(true);
    try {
      const params = new URLSearchParams({
        candidate_id: candidateId,
        company_name: hrCompanyName,
        frontend_url: window.location.origin
      });
      
      if (selectedJob) {
        params.append('job_id', selectedJob);
      }

      const response = await fetch(`${API_BASE_URL}/candidates/invite-assessment?${params.toString()}`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' })
      });

      if (!response.ok) throw new Error('Failed to send invitation');
      
      const data = await response.json();
      alert(`Assessment invitation sent to ${data.candidate_name}`);
      setInviteModalOpen(false);
      setSelectedJob('');
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Failed to send invitation. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  const handleSendInterviewInvite = async () => {
    // Validate required fields
    if (!interviewData.interviewDate || !interviewData.interviewTime) {
      alert('Please provide interview date and time');
      return;
    }

    setInviting(true);
    try {
      const payload = {
        candidate_id: candidateId,
        company_name: hrCompanyName,
        job_id: interviewData.jobId || null,
        interview_date: interviewData.interviewDate,
        interview_time: interviewData.interviewTime,
        meeting_link: interviewData.meetingLink || null,
        additional_notes: interviewData.additionalNotes || null
      };

      const response = await fetch(`${API_BASE_URL}/candidates/invite-interview`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send interview invitation');
      }
      
      const data = await response.json();
      alert(`Interview invitation sent to ${data.candidate_name}`);
      setInterviewModalOpen(false);
      setInterviewData({
        jobId: '',
        interviewDate: '',
        interviewTime: '',
        meetingLink: '',
        additionalNotes: ''
      });
    } catch (error) {
      console.error('Error sending interview invitation:', error);
      alert(error.message || 'Failed to send interview invitation. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  const calculateExperience = (workExperience) => {
    if (!workExperience || workExperience.length === 0) return 0;
    
    let totalYears = 0;
    workExperience.forEach(exp => {
      if (exp.startDate) {
        const start = new Date(exp.startDate);
        const end = exp.current ? new Date() : new Date(exp.endDate || new Date());
        const years = (end - start) / (1000 * 60 * 60 * 24 * 365);
        totalYears += years;
      }
    });
    
    return Math.round(totalYears * 10) / 10;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
        <Header variant="dashboard" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading profile...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
        <Header variant="dashboard" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="person_off" size={64} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Candidate not found</p>
            <Button onClick={() => navigate('/hr/candidates')} className="mt-4">
              Back to Candidates
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const profile = candidate.profile || {};
  const experience = calculateExperience(profile.work_experience);
  const experienceDisplay = experience === 0 ? 'Fresher' : experience <= 1 ? '0-1 years (Fresher)' : `${experience} years`;

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
      <Header variant="dashboard" />
      
      <main className="flex-1 px-4 py-6 sm:px-6 lg:px-10">
        {/* Back Button */}
        <button
          onClick={() => navigate('/hr/candidates')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <Icon name="arrow_back" size={20} className="mr-2" />
          Back to Candidates
        </button>

        {/* Profile Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                {candidate.profile_picture ? (
                  <img src={candidate.profile_picture} alt={profile.full_name} className="w-24 h-24 rounded-full object-cover" />
                ) : (
                  <span className="text-4xl font-bold text-primary">
                    {candidate.first_name?.charAt(0)}{candidate.last_name?.charAt(0)}
                  </span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-3xl font-bold text-gray-900">{profile.full_name || `${candidate.first_name} ${candidate.last_name}`}</h1>
                  {isVerified && (
                    <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-full">
                      <Icon name="verified" size={18} className="text-green-600" />
                      <span className="text-sm font-semibold text-green-600">Verified</span>
                    </div>
                  )}
                </div>
                <p className="text-lg text-gray-600 mt-1">{profile.job_role || 'Job Seeker'}</p>
                <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Icon name="email" size={16} />
                    {candidate.email}
                  </span>
                  {profile.phone && (
                    <span className="flex items-center gap-1">
                      <Icon name="phone" size={16} />
                      {profile.phone}
                    </span>
                  )}
                  {profile.location && (
                    <span className="flex items-center gap-1">
                      <Icon name="location_on" size={16} />
                      {profile.location}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setInterviewModalOpen(true)} variant="primary">
                <Icon name="video_call" size={20} className="mr-2" />
                Invite for Interview
              </Button>
              <Button onClick={() => setInviteModalOpen(true)} variant="outline">
                <Icon name="mail" size={20} className="mr-2" />
                Invite for Assessment
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Skills */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Skills</h2>
              {profile.skills && profile.skills.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="lightbulb" size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No skills added yet</p>
                </div>
              )}
            </div>

            {/* Work Experience */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Work Experience</h2>
              {profile.work_experience && profile.work_experience.length > 0 ? (
                <div className="space-y-4">
                  {profile.work_experience.map((exp, index) => (
                    <div key={index} className="border-l-2 border-primary pl-4">
                      <h3 className="font-semibold text-gray-900">{exp.position}</h3>
                      <p className="text-gray-600">{exp.company}</p>
                      <p className="text-sm text-gray-500">
                        {exp.startDate} - {exp.current ? 'Present' : exp.endDate}
                        {exp.location && ` • ${exp.location}`}
                      </p>
                      {exp.description && (
                        <p className="mt-2 text-gray-700">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="work" size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No work experience added yet</p>
                </div>
              )}
            </div>

            {/* Education */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Education</h2>
              {profile.education && profile.education.length > 0 ? (
                <div className="space-y-4">
                  {profile.education.map((edu, index) => {
                    const institution = edu.institution === 'Other' ? edu.institutionOther : edu.institution;
                    const degree = edu.degree === 'Other' ? edu.degreeOther : edu.degree;
                    const field = edu.field === 'Other' ? edu.fieldOther : edu.field;
                    
                    return (
                      <div key={index} className="border-l-2 border-primary pl-4">
                        <h3 className="font-semibold text-gray-900">{degree || 'Degree'} {field ? `in ${field}` : ''}</h3>
                        <p className="text-gray-600">{institution || 'Institution'}</p>
                        <p className="text-sm text-gray-500">
                          {edu.startDate} - {edu.endDate}
                          {edu.gpa && ` • GPA: ${edu.gpa}`}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="school" size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No education history added yet</p>
                </div>
              )}
            </div>

            {/* Projects */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Projects</h2>
              {profile.projects && profile.projects.length > 0 ? (
                <div className="space-y-4">
                  {profile.projects.map((project, index) => (
                    <div key={index} className="border-l-2 border-primary pl-4">
                      <h3 className="font-semibold text-gray-900">{project.title}</h3>
                      <p className="text-gray-700 mt-1">{project.description}</p>
                      {project.technologies && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(typeof project.technologies === 'string' ? project.technologies.split(',') : project.technologies).map((tech, i) => (
                            <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                              {typeof tech === 'string' ? tech.trim() : tech}
                            </span>
                          ))}
                        </div>
                      )}
                      {project.link && (
                        <a href={project.link} target="_blank" rel="noopener noreferrer" className="text-primary text-sm mt-2 inline-flex items-center gap-1">
                          View Project <Icon name="open_in_new" size={14} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="code" size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No projects added yet</p>
                </div>
              )}
            </div>

            {/* Languages & Hobbies */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Languages</h2>
                {profile.languages && profile.languages.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profile.languages.map((lang, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {typeof lang === 'string' ? lang : lang.name || lang}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No languages specified</p>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Certifications</h2>
                {profile.courses && profile.courses.length > 0 ? (
                  <ul className="space-y-2">
                    {profile.courses.map((course, index) => (
                      <li key={index} className="text-sm text-gray-700">
                        • {typeof course === 'string' ? course : course.name || course}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">No certifications specified</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Summary & Applications */}
          <div className="space-y-6">
            {/* Profile Completeness Alert */}
            {(!profile.skills || profile.skills.length === 0) && 
             (!profile.work_experience || profile.work_experience.length === 0) && 
             (!profile.education || profile.education.length === 0) && 
             (!profile.projects || profile.projects.length === 0) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <Icon name="info" size={24} className="text-yellow-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-1">Profile Incomplete</h3>
                    <p className="text-sm text-yellow-800">
                      This candidate hasn't completed their profile yet. Consider inviting them to complete their profile before assessment.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Summary</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Experience</p>
                  <p className="font-semibold text-gray-900">{experienceDisplay}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-semibold text-gray-900">{profile.location || 'Not specified'}</p>
                </div>
                {profile.languages && profile.languages.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500">Languages</p>
                    <p className="font-semibold text-gray-900">
                      {profile.languages.map(lang => typeof lang === 'string' ? lang : lang.name || lang).join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">External Links</h2>
              {(profile.linkedin || profile.github || profile.portfolio || profile.resume_url) ? (
                <div className="space-y-3">
                  {profile.linkedin && (
                    <a 
                      href={profile.linkedin.startsWith('http') ? profile.linkedin : `https://${profile.linkedin}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
                    >
                      <Icon name="link" className="text-blue-600" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 mb-1">LinkedIn</p>
                        <p className="text-sm font-medium text-blue-600 truncate group-hover:text-blue-700">
                          {profile.linkedin}
                        </p>
                      </div>
                      <Icon name="open_in_new" size={18} className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  )}
                  {profile.github && (
                    <a 
                      href={profile.github.startsWith('http') ? profile.github : `https://${profile.github}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                    >
                      <Icon name="code" className="text-gray-700" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 mb-1">GitHub</p>
                        <p className="text-sm font-medium text-gray-700 truncate group-hover:text-gray-900">
                          {profile.github}
                        </p>
                      </div>
                      <Icon name="open_in_new" size={18} className="text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  )}
                  {profile.portfolio && (
                    <a 
                      href={profile.portfolio.startsWith('http') ? profile.portfolio : `https://${profile.portfolio}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors group"
                    >
                      <Icon name="language" className="text-purple-600" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 mb-1">Portfolio</p>
                        <p className="text-sm font-medium text-purple-600 truncate group-hover:text-purple-700">
                          {profile.portfolio}
                        </p>
                      </div>
                      <Icon name="open_in_new" size={18} className="text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  )}
                  {profile.resume_url && (
                    <button 
                      onClick={() => {
                        // Handle base64 data URI
                        if (profile.resume_url.startsWith('data:')) {
                          const newWindow = window.open('', '_blank');
                          if (newWindow) {
                            newWindow.document.write(`
                              <html>
                                <head>
                                  <title>${profile.resume_filename || 'Resume'}</title>
                                  <style>
                                    body { margin: 0; padding: 0; }
                                    iframe { width: 100%; height: 100vh; border: none; }
                                  </style>
                                </head>
                                <body>
                                  <iframe src="${profile.resume_url}"></iframe>
                                </body>
                              </html>
                            `);
                            newWindow.document.close();
                          }
                        } else {
                          window.open(profile.resume_url, '_blank');
                        }
                      }}
                      className="flex items-center gap-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors group w-full text-left"
                    >
                      <Icon name="description" className="text-green-600" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 mb-1">Resume</p>
                        <p className="text-sm font-medium text-green-600 truncate group-hover:text-green-700">
                          {profile.resume_filename || 'View Resume'}
                        </p>
                      </div>
                      <Icon name="open_in_new" size={18} className="text-green-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No external links provided</p>
              )}
            </div>

            {/* Applications */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Applications</h2>
              {candidate.applications && candidate.applications.length > 0 ? (
                <div className="space-y-3">
                  {candidate.applications.map((app) => (
                    <div key={app.id} className="border-l-2 border-primary pl-3">
                      <p className="font-semibold text-gray-900">{app.job_title}</p>
                      <p className="text-sm text-gray-600">{app.company}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          app.status === 'offer' ? 'bg-green-100 text-green-700' :
                          app.status === 'interview' ? 'bg-blue-100 text-blue-700' :
                          app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {app.status}
                        </span>
                        {app.ats_score && (
                          <span className={`text-xs font-semibold ${app.ats_score >= 70 ? 'text-green-600' : 'text-amber-600'}`}>
                            ATS: {app.ats_score.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No applications yet</p>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Interview Invitation Modal */}
      {interviewModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Invite for Interview</h3>
              <button
                onClick={() => setInterviewModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="close" size={24} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Sending invitation to: <span className="font-semibold">{profile.full_name || `${candidate.first_name} ${candidate.last_name}`}</span>
              </p>
              <p className="text-sm text-gray-600">
                Email: <span className="font-semibold">{candidate.email}</span>
              </p>
            </div>

            <div className="space-y-4">
              {/* Job Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Job Position (Optional)
                </label>
                <select
                  value={interviewData.jobId}
                  onChange={(e) => setInterviewData({...interviewData, jobId: e.target.value})}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">General Interview</option>
                  {jobs.filter(job => job.status === 'Active').map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Interview Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interview Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={interviewData.interviewDate}
                  onChange={(e) => setInterviewData({...interviewData, interviewDate: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              {/* Interview Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interview Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={interviewData.interviewTime}
                  onChange={(e) => setInterviewData({...interviewData, interviewTime: e.target.value})}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              {/* Google Meet Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Google Meet Link (Optional)
                </label>
                <input
                  type="url"
                  value={interviewData.meetingLink}
                  onChange={(e) => setInterviewData({...interviewData, meetingLink: e.target.value})}
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Provide a Google Meet, Zoom, or any video conferencing link
                </p>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={interviewData.additionalNotes}
                  onChange={(e) => setInterviewData({...interviewData, additionalNotes: e.target.value})}
                  placeholder="Any special instructions or information for the candidate..."
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setInterviewModalOpen(false)}
                className="flex-1"
                disabled={inviting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSendInterviewInvite}
                className="flex-1"
                disabled={inviting || !interviewData.interviewDate || !interviewData.interviewTime}
              >
                {inviting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {inviteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Invite for Assessment</h3>
              <button
                onClick={() => setInviteModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="close" size={24} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Sending invitation to: <span className="font-semibold">{profile.full_name || `${candidate.first_name} ${candidate.last_name}`}</span>
              </p>
              <p className="text-sm text-gray-600">
                Email: <span className="font-semibold">{candidate.email}</span>
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Job Position (Optional)
              </label>
              <select
                value={selectedJob}
                onChange={(e) => setSelectedJob(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">General Assessment</option>
                {jobs.filter(job => job.status === 'Active').map((job) => {
                  // Check if candidate already applied to this job
                  const application = candidateApplications.find(app => app.job_id === job.id);
                  const hasApplied = !!application;
                  const atsScore = application?.ats_score;
                  
                  return (
                    <option key={job.id} value={job.id}>
                      {job.title}
                      {hasApplied && atsScore ? ` (Applied - ATS: ${atsScore.toFixed(1)}%)` : hasApplied ? ' (Applied)' : ''}
                    </option>
                  );
                })}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Jobs marked with "Applied" show the candidate already applied with their ATS score
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setInviteModalOpen(false)}
                className="flex-1"
                disabled={inviting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSendInvite}
                className="flex-1"
                disabled={inviting}
              >
                {inviting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default HrCandidateProfilePage;
